import { useCallback } from 'react';
import {
    useQuery,
    useInfiniteQuery,
    useMutation,
    useQueryClient,
    keepPreviousData,
    matchQuery,
    type QueryClient
} from '@tanstack/react-query';
import {
    fetchCharges,
    fetchCharge,
    createCharge,
    processCharge,
    cancelCharge,
    updateCharge,
    type UpdateChargeChanges,
    retryCharge,
    fetchOneTimeDiscounts,
    createOneTimeDiscount,
    linkOneTimeDiscount,
    unlinkOneTimeDiscount,
    setChargeOneTimeDiscounts,
    deleteOneTimeDiscount,
    fetchInvoices,
    fetchInvoice,
    voidInvoice,
    fetchBillingOverview,
    fetchProducts,
    fetchProductTaxonomy,
    fetchPracticeOptions,
    createProduct,
    updateProduct,
    createProductVersion,
    updateProductVersion,
    deleteProductVersion,
    createDiscountRule,
    updateDiscountRule,
    deleteDiscountRule,
    fetchPracticeDiscountRules,
    setPracticeRuleEligibility,
    type ChargesListParams,
    type CreateChargeBody,
    type CreateOneTimeDiscountBody,
    type OneTimeDiscountsListParams,
    type DiscountRuleBody,
    type CreateProductBody,
    type InvoicesListParams,
    type ProductVersion,
    type UpdateProductBody,
    type VoidInvoiceBody
} from '../services/billing';
import { fetchPracticeProductPricing } from '../services/practiceProductPricing';
import { resolveProductImageUrls } from '../services/productImages';
import { planVersionWrites } from '../services/productVersionPlan';
import type { VersionChanges, VersionWrite } from '../services/productVersionPlan';
import { rangeBounds, type RangeKey } from './useBillingRange';
import { invalidateSidebarCounts } from './useSidebarCounts';
import {
    BILLING_CHARGES_KEY,
    BILLING_CHARGES_INFINITE_KEY,
    BILLING_CHARGE_KEY,
    BILLING_ONE_TIME_DISCOUNTS_KEY,
    BILLING_ONE_TIME_DISCOUNTS_INFINITE_KEY,
    BILLING_INVOICES_KEY,
    BILLING_INVOICES_INFINITE_KEY,
    BILLING_INVOICE_KEY,
    PRODUCTS_IMAGES_KEY,
    PRODUCTS_KEY,
    PRODUCTS_TAXONOMY_KEY
} from './queryKeys';

export function useCharges(params: ChargesListParams & { enabled?: boolean }) {
    const queryClient = useQueryClient();
    const { enabled = true, ...rest } = params;

    const queryKey = [
        BILLING_CHARGES_KEY,
        rest.page,
        rest.limit,
        rest.q ?? '',
        rest.practiceId ?? '',
        rest.status ?? '',
        rest.createdVia ?? '',
        rest.sort ?? '',
        rest.order ?? ''
    ];

    const { data, isLoading, isFetching, isError } = useQuery({
        queryKey,
        queryFn: () => fetchCharges(rest),
        staleTime: 60 * 1000,
        placeholderData: keepPreviousData,
        enabled
    });

    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
    }, [queryClient]);

    return {
        charges: data?.data ?? [],
        meta: data?.meta ?? { total: 0, page: rest.page, limit: rest.limit },
        isLoading,
        isFetching,
        isError,
        refresh
    };
}

/**
 * Same endpoint as `useCharges`, accumulated instead of sliced -
 * `DataTable`'s `'infinite'` mode. The query key omits `page` on purpose,
 * same as `useInfiniteInvoices`: every other param change is a new key, so a
 * filter/search/sort change starts a fresh page-1 fetch rather than
 * appending onto stale rows.
 */
export function useInfiniteCharges(
    params: Omit<ChargesListParams, 'page'> & { enabled?: boolean }
) {
    const { enabled = true, ...rest } = params;
    const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useInfiniteQuery({
            queryKey: [
                ...BILLING_CHARGES_INFINITE_KEY,
                rest.limit,
                rest.q ?? '',
                rest.practiceId ?? '',
                rest.status ?? '',
                rest.createdVia ?? '',
                rest.sort ?? '',
                rest.order ?? ''
            ],
            initialPageParam: 1,
            queryFn: ({ pageParam }) => fetchCharges({ ...rest, page: pageParam as number }),
            getNextPageParam: (lastPage) =>
                lastPage.meta.page * lastPage.meta.limit < lastPage.meta.total
                    ? lastPage.meta.page + 1
                    : undefined,
            staleTime: 60 * 1000,
            placeholderData: keepPreviousData,
            enabled
        });

    const firstPageMeta = data?.pages[0]?.meta;

    return {
        charges: data?.pages.flatMap((page) => page.data) ?? [],
        meta: firstPageMeta ?? { total: 0, page: 1, limit: rest.limit },
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage
    };
}

export function useCharge(id: string) {
    const queryClient = useQueryClient();

    const { data, isLoading, isError } = useQuery({
        queryKey: BILLING_CHARGE_KEY(id),
        queryFn: () => fetchCharge(id),
        staleTime: 60 * 1000,
        enabled: Boolean(id)
    });

    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(id) });
    }, [queryClient, id]);

    return {
        charge: data,
        isLoading,
        isError,
        refresh
    };
}

// Charge/invoice mutations move money between states; the billing overview
// (org-wide and practice-scoped) aggregates the same rows, so it refreshes
// alongside the entity caches.
function invalidateBillingOverview(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({ queryKey: ['v2/billing/overview'] });
}

export function useCreateCharge() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: CreateChargeBody) => createCharge(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            invalidateBillingOverview(queryClient);
            void invalidateSidebarCounts(queryClient);
        }
    });
}

export function useProcessCharge() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: { id: string }) => processCharge(id),
        onSuccess: (_data, { id }) => {
            queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(id) });
            queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            queryClient.invalidateQueries({ queryKey: [BILLING_ONE_TIME_DISCOUNTS_KEY] });
            invalidateBillingOverview(queryClient);
            void invalidateSidebarCounts(queryClient);
        }
    });
}

export function useUpdateCharge() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, changes }: { id: string; changes: UpdateChargeChanges }) =>
            updateCharge(id, changes),
        onSuccess: (_data, { id }) => {
            queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(id) });
            queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            invalidateBillingOverview(queryClient);
        }
    });
}

export function useCancelCharge() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelCharge(id, reason),
        onSuccess: (_data, { id }) => {
            queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(id) });
            queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            // Canceling auto-unlinks the charge's one-time discounts.
            queryClient.invalidateQueries({ queryKey: [BILLING_ONE_TIME_DISCOUNTS_KEY] });
            invalidateBillingOverview(queryClient);
            void invalidateSidebarCounts(queryClient);
        }
    });
}

export function useRetryCharge() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: { id: string }) => retryCharge(id),
        onSuccess: (_data, { id }) => {
            queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(id) });
            queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            queryClient.invalidateQueries({ queryKey: [BILLING_ONE_TIME_DISCOUNTS_KEY] });
            invalidateBillingOverview(queryClient);
            void invalidateSidebarCounts(queryClient);
        }
    });
}

export function useOneTimeDiscounts(params: OneTimeDiscountsListParams, enabled = true) {
    const { data, isLoading, isFetching } = useQuery({
        queryKey: [
            BILLING_ONE_TIME_DISCOUNTS_KEY,
            params.page,
            params.limit,
            params.q ?? '',
            params.status?.join(',') ?? '',
            params.practiceId ?? '',
            params.patientId ?? '',
            params.includeUnassigned ?? false,
            params.linked ?? null,
            params.sort ?? '',
            params.order ?? ''
        ],
        queryFn: () => fetchOneTimeDiscounts(params),
        staleTime: 60 * 1000,
        placeholderData: keepPreviousData,
        enabled
    });

    return {
        oneTimeDiscounts: data?.data ?? [],
        meta: data?.meta ?? { total: 0, page: params.page, limit: params.limit },
        isLoading,
        isFetching
    };
}

/**
 * Same endpoint as `useOneTimeDiscounts`, accumulated instead of sliced -
 * `DataTable`'s `'infinite'` mode. Same page-omitted query key convention as
 * `useInfiniteCharges`/`useInfiniteInvoices`.
 */
export function useInfiniteOneTimeDiscounts(
    params: Omit<OneTimeDiscountsListParams, 'page'> & { enabled?: boolean }
) {
    const { enabled = true, ...rest } = params;
    const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useInfiniteQuery({
            queryKey: [
                ...BILLING_ONE_TIME_DISCOUNTS_INFINITE_KEY,
                rest.limit,
                rest.q ?? '',
                rest.status?.join(',') ?? '',
                rest.practiceId ?? '',
                rest.patientId ?? '',
                rest.includeUnassigned ?? false,
                rest.linked ?? null,
                rest.sort ?? '',
                rest.order ?? ''
            ],
            initialPageParam: 1,
            queryFn: ({ pageParam }) =>
                fetchOneTimeDiscounts({ ...rest, page: pageParam as number }),
            getNextPageParam: (lastPage) =>
                lastPage.meta.page * lastPage.meta.limit < lastPage.meta.total
                    ? lastPage.meta.page + 1
                    : undefined,
            staleTime: 60 * 1000,
            placeholderData: keepPreviousData,
            enabled
        });

    const firstPageMeta = data?.pages[0]?.meta;

    return {
        oneTimeDiscounts: data?.pages.flatMap((page) => page.data) ?? [],
        meta: firstPageMeta ?? { total: 0, page: 1, limit: rest.limit },
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage
    };
}

export function useCreateOneTimeDiscount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: CreateOneTimeDiscountBody) => createOneTimeDiscount(body),
        onSuccess: (_data, body) => {
            void queryClient.invalidateQueries({ queryKey: [BILLING_ONE_TIME_DISCOUNTS_KEY] });
            // A linked or sourced one-time discount changes charge rows (payable
            // total / issuedOneTimeDiscountsCount), so refresh those too.
            if (body.chargeId || body.sourceChargeId) {
                void queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            }
            if (body.chargeId) {
                void queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(body.chargeId) });
            }
        }
    });
}

// Linking and unlinking change the charge's payable total, so the charge
// list and the affected charge's detail view are invalidated too.
export function useLinkOneTimeDiscount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, chargeId }: { id: string; chargeId: string }) =>
            linkOneTimeDiscount(id, chargeId),
        onSuccess: (_data, { chargeId }) => {
            void queryClient.invalidateQueries({ queryKey: [BILLING_ONE_TIME_DISCOUNTS_KEY] });
            void queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            void queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(chargeId) });
        }
    });
}

export function useSetChargeOneTimeDiscounts() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            chargeId,
            oneTimeDiscountIds
        }: {
            chargeId: string;
            oneTimeDiscountIds: string[];
        }) => setChargeOneTimeDiscounts(chargeId, oneTimeDiscountIds),
        onSuccess: (_data, { chargeId }) => {
            void queryClient.invalidateQueries({ queryKey: [BILLING_ONE_TIME_DISCOUNTS_KEY] });
            void queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            void queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(chargeId) });
        }
    });
}

export function useDeleteOneTimeDiscount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: { id: string }) => deleteOneTimeDiscount(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [BILLING_ONE_TIME_DISCOUNTS_KEY] });
            // Deleting a sourced one-time discount frees its source charge to
            // generate a new one.
            void queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
        }
    });
}

export function useUnlinkOneTimeDiscount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: { id: string; chargeId: string | null }) => unlinkOneTimeDiscount(id),
        onSuccess: (_data, { chargeId }) => {
            void queryClient.invalidateQueries({ queryKey: [BILLING_ONE_TIME_DISCOUNTS_KEY] });
            void queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            if (chargeId) {
                void queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(chargeId) });
            }
        }
    });
}

export function useBillingOverview(range: RangeKey, practiceId?: string) {
    const { data, isLoading, isFetching, isError } = useQuery({
        // Keyed on the preset, not its dates: bounds resolve at fetch time so revisits hit the cache.
        queryKey: ['v2/billing/overview', range, practiceId ?? 'all'],
        queryFn: () => {
            const { from, to } = rangeBounds(range);
            return fetchBillingOverview(from?.toISOString() ?? null, to.toISOString(), practiceId);
        },
        staleTime: 60 * 1000,
        placeholderData: keepPreviousData
    });

    return {
        overview: data,
        isLoading,
        isFetching,
        isError
    };
}

export function useInvoices(params: InvoicesListParams & { enabled?: boolean }) {
    const { enabled = true, ...rest } = params;
    const { data, isLoading, isFetching } = useQuery({
        queryKey: [
            BILLING_INVOICES_KEY,
            rest.page,
            rest.limit,
            rest.q ?? '',
            rest.status ?? '',
            rest.practiceId ?? '',
            rest.sort ?? '',
            rest.order ?? ''
        ],
        queryFn: () => fetchInvoices(rest),
        staleTime: 60 * 1000,
        placeholderData: keepPreviousData,
        enabled
    });

    return {
        invoices: data?.data ?? [],
        meta: data?.meta ?? { total: 0, page: rest.page, limit: rest.limit },
        isLoading,
        isFetching
    };
}

/**
 * Same endpoint, accumulated instead of sliced - `DataTable`'s `'infinite'`
 * mode. The query key omits `page` on purpose: every other param change
 * (search, status, practice, sort) is a new key, so TanStack Query starts a
 * fresh page-1 fetch and drops the previously accumulated pages rather than
 * appending the new chunk after stale rows.
 */
export function useInfiniteInvoices(
    params: Omit<InvoicesListParams, 'page'> & { enabled?: boolean }
) {
    const { enabled = true, ...rest } = params;
    const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useInfiniteQuery({
            queryKey: [
                BILLING_INVOICES_INFINITE_KEY,
                rest.limit,
                rest.q ?? '',
                rest.status ?? '',
                rest.practiceId ?? '',
                rest.sort ?? '',
                rest.order ?? ''
            ],
            initialPageParam: 1,
            queryFn: ({ pageParam }) => fetchInvoices({ ...rest, page: pageParam as number }),
            getNextPageParam: (lastPage) =>
                lastPage.meta.page * lastPage.meta.limit < lastPage.meta.total
                    ? lastPage.meta.page + 1
                    : undefined,
            staleTime: 60 * 1000,
            placeholderData: keepPreviousData,
            enabled
        });

    const firstPageMeta = data?.pages[0]?.meta;

    return {
        invoices: data?.pages.flatMap((page) => page.data) ?? [],
        meta: firstPageMeta ?? { total: 0, page: 1, limit: rest.limit },
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage
    };
}

export function useInvoice(id: string) {
    const { data, isLoading, isError } = useQuery({
        queryKey: BILLING_INVOICE_KEY(id),
        queryFn: () => fetchInvoice(id),
        staleTime: 60 * 1000,
        enabled: Boolean(id)
    });

    return {
        invoice: data,
        isLoading,
        isError
    };
}

// Voiding changes the parent charge's invoice roll-up, and can mint a
// one-time discount, so those caches are refreshed alongside the invoice itself.
export function useVoidInvoice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, body }: { id: string; body: VoidInvoiceBody; chargeId: string }) =>
            voidInvoice(id, body),
        onSuccess: (_data, { id, body, chargeId }) => {
            void queryClient.invalidateQueries({ queryKey: [BILLING_INVOICES_KEY] });
            void queryClient.invalidateQueries({ queryKey: [BILLING_INVOICES_INFINITE_KEY] });
            void queryClient.invalidateQueries({ queryKey: BILLING_INVOICE_KEY(id) });
            void queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            void queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(chargeId) });
            invalidateBillingOverview(queryClient);
            if (body.createOneTimeDiscount) {
                void queryClient.invalidateQueries({ queryKey: [BILLING_ONE_TIME_DISCOUNTS_KEY] });
            }
        }
    });
}

// Stable identity: the fallback feeds render-time option lists.
const EMPTY_TAXONOMY_LIST: string[] = [];

export function useProducts(params?: {
    sort?: 'name' | 'category' | 'date';
    order?: 'asc' | 'desc';
}) {
    const { data, isLoading } = useQuery({
        queryKey: [PRODUCTS_KEY, params],
        queryFn: () => fetchProducts(params),
        staleTime: 5 * 60 * 1000
    });

    return {
        products: data ?? [],
        isLoading
    };
}

// Server-owned vocabularies for the product form's category/vendor pickers.
// Code-defined server-side, so it is cached for the session rather than refetched.
const TAXONOMY_STALE_TIME = 24 * 60 * 60 * 1000;

export function useProductTaxonomy() {
    const { data, isLoading, isError } = useQuery({
        queryKey: PRODUCTS_TAXONOMY_KEY,
        queryFn: fetchProductTaxonomy,
        staleTime: TAXONOMY_STALE_TIME,
        gcTime: TAXONOMY_STALE_TIME
    });

    return {
        categories: data?.categories ?? EMPTY_TAXONOMY_LIST,
        vendors: data?.vendors ?? EMPTY_TAXONOMY_LIST,
        isLoading,
        isError
    };
}

// Signed URLs live 7 days server-side; keep them just short of that so a
// cached entry is never handed out after it has expired.
const IMAGE_URL_STALE_TIME = 6 * 24 * 60 * 60 * 1000;

// Stable identity: callers feed the map into useMemo dependency lists.
const NO_IMAGE_URLS: Record<string, string> = {};

// Keyed on the joined ids, not the array: an identically-valued new array on
// every render would otherwise refetch forever.
export function useProductImageUrls(fileIds: string[]) {
    const key = fileIds.join(',');

    const { data, isLoading } = useQuery({
        queryKey: [PRODUCTS_IMAGES_KEY, key],
        queryFn: () => resolveProductImageUrls(key.length > 0 ? key.split(',') : []),
        enabled: key.length > 0,
        staleTime: IMAGE_URL_STALE_TIME
    });

    return {
        imageUrls: data ?? NO_IMAGE_URLS,
        isLoading: key.length > 0 && isLoading
    };
}

export function usePracticeOptions(enabled = true) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['v2/practices'],
        queryFn: fetchPracticeOptions,
        staleTime: 5 * 60 * 1000,
        enabled
    });

    return {
        practices: data ?? [],
        isLoading,
        isError
    };
}

export function useCreateProduct() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: CreateProductBody) => createProduct(body),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
        }
    });
}

export function useUpdateProduct() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, body }: { id: string; body: UpdateProductBody }) =>
            updateProduct(id, body),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
        }
    });
}

// null for writes that leave no comparable version (a park or a delete).
async function applyVersionWrite(
    productId: string,
    write: VersionWrite
): Promise<ProductVersion | null> {
    if (write.kind === 'park') {
        await updateProductVersion(productId, write.productVersionId, { label: write.label });
        return null;
    }
    if (write.kind === 'update') {
        return updateProductVersion(productId, write.productVersionId, write.body);
    }
    if (write.kind === 'create') {
        return createProductVersion(productId, write.body);
    }
    await deleteProductVersion(productId, write.productVersionId);
    return null;
}

// Applies a batch of version changes for one product, then refreshes the
// catalog once. Sequential and ordered by planVersionWrites: every call is
// validated against the state the previous one left behind. Returns the
// saved update/create versions in `changes.update` then `changes.create`
// order, so a caller can verify a requested field actually landed.
export function useSaveProductVersions() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            productId,
            changes
        }: {
            productId: string;
            changes: VersionChanges;
        }): Promise<ProductVersion[]> => {
            const saved: ProductVersion[] = [];
            for (const write of planVersionWrites(changes)) {
                const result = await applyVersionWrite(productId, write);
                if (result) saved.push(result);
            }
            return saved;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
        }
    });
}

// Create (no ruleId) or update (ruleId set) a discount rule on a product.
// Rules ride on the products payload, and pricing depends on eligibility,
// so both the catalog and practice-scoped queries refresh.
export function useSaveDiscountRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            productId,
            ruleId,
            body
        }: {
            productId: string;
            ruleId?: string;
            body: Partial<DiscountRuleBody>;
        }) =>
            ruleId
                ? updateDiscountRule(productId, ruleId, body)
                : createDiscountRule(productId, body as DiscountRuleBody),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
            void queryClient.invalidateQueries({ queryKey: ['v2/practices'] });
        }
    });
}

export function useDeleteDiscountRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ productId, ruleId }: { productId: string; ruleId: string }) =>
            deleteDiscountRule(productId, ruleId),
        onSuccess: (_data, { productId, ruleId }) => {
            const deletedRuleKey = [PRODUCTS_KEY, productId, 'discount-rules', ruleId];
            // The still-open modal observes the deleted rule's roster; refetching it would 404.
            void queryClient.invalidateQueries({
                queryKey: [PRODUCTS_KEY],
                predicate: (query) => !matchQuery({ queryKey: deletedRuleKey }, query)
            });
            void queryClient.invalidateQueries({ queryKey: ['v2/practices'] });
        }
    });
}

// Every rule in the catalog with this practice's eligibility flag - the
// practice-side counterpart of the rule roster editor.
export function usePracticeDiscountRules(practiceId: string | null) {
    const { data, isLoading } = useQuery({
        queryKey: ['v2/practices', practiceId, 'discount-rules'],
        queryFn: () => fetchPracticeDiscountRules(practiceId as string),
        enabled: Boolean(practiceId),
        staleTime: 60 * 1000
    });

    return {
        rules: data ?? [],
        isLoading: isLoading && Boolean(practiceId)
    };
}

export function useSetPracticeRuleEligibility() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            practiceId,
            ruleId,
            eligible
        }: {
            practiceId: string;
            ruleId: string;
            eligible: boolean;
        }) => setPracticeRuleEligibility(practiceId, ruleId, eligible),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
            void queryClient.invalidateQueries({ queryKey: ['v2/practices'] });
        }
    });
}

// Every product priced for one practice (list price + resolved discount), for
// the Accounts practice detail Pricing tab.
export function usePracticeProductPricing(practiceId: string | null) {
    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['v2/practices', practiceId, 'product-pricing'],
        queryFn: () => fetchPracticeProductPricing(practiceId as string),
        enabled: Boolean(practiceId),
        staleTime: 60 * 1000
    });

    return {
        pricedProducts: data ?? [],
        isLoading: isLoading && Boolean(practiceId),
        isFetching
    };
}
